<template>
  <v-row id="service-cloud-usage">
    <v-col cols="6" class="pt-6">
      <loader :loading="isLoading" />
      <usage-card title="Ticket Usage" :usageValue="ticketsUsage">
        <template #chart>
          <account-usage-donut-chart
            :data="_usageChartData"
            :background-colors="_backgroundColors"
            :inner-label="`Usage ${ticketsUsagePercentage}%`"
          />
        </template>
      </usage-card>
    </v-col>
  </v-row>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import isEmpty from "lodash/isEmpty";

import accounts from "@core/services/accounts.js";
import Loader from "@core/components/shared/Loader.vue";
import UsageCard from "@core/views/self_service/usage/UsageCard.vue";

import AccountUsageDonutChart from "@core/views/self_service/usage/AccountUsageDonutChart.vue";
import { getSelectedFilterDayDate } from "@core/config/utils.js";

import constants from "@core/config/constants.js";
const { PRODUCT_IDENTFIERS } = constants;

import UsageMixin from "@core/mixins/UsageMixin.js";

export default {
  name: "ServiceCloudUsage",
  /**
  |--------------------------------------------------
  | Props
  |--------------------------------------------------
  */
  props: {
    days: { type: Number, default: 0, required: true }
  },
  /**
  |--------------------------------------------------
  | Mixins
  |--------------------------------------------------
  */
  mixins: [UsageMixin],
  /**
  |--------------------------------------------------
  | Data Properties
  |--------------------------------------------------
  */
  data() {
    return {
      isLoading: false,
      statsKey: "ticketsStats",
      ticketsStats: { allocated: 0, used: 0 }
    };
  },
  /**
  |--------------------------------------------------
  | Computed Propertie
  |--------------------------------------------------
  */
  computed: {
    ...mapGetters({
      account: "auth/account",
      serviceCloudPackage: "auth/serviceCloudPackage"
    }),
    currentAccount() {
      if (isEmpty(this.account.list)) return null;
      return this.account.list.find(acc => acc.id == this.account.id);
    },
    ticketsUsage() {
      return `${this.ticketsStats.used} of ${this.ticketsStats.allocated ||
        0} tickets`;
    },
    ticketsUsagePercentage() {
      return Math.ceil(
        this.$options.filters.calculatePercentage(
          this.ticketsStats.used,
          this.serviceCloudPackage.alloted_volume
        )
      );
    },
    payload() {
      const filterDates = this.getSelectedFilterDayDate(this.days);
      return {
        page: 1,
        ...filterDates,
        volume_type_eq: PRODUCT_IDENTFIERS.msc.type,
        per_page:
          this.days == 365
            ? moment().isLeapYear()
              ? 366
              : 365
            : moment(filterDates.created_on_gteq).daysInMonth()
      };
    }
  },
  /**
  |--------------------------------------------------
  | Watching Properties
  |--------------------------------------------------
  */
  watch: {
    days() {
      this.getTicketsUsage();
    },
    serviceCloudPackage: {
      handler(val) {
        this.ticketsStats.allocated = val.alloted_volume || 0;
      },
      immediate: true,
      deep: true
    }
  },
  /**
  |--------------------------------------------------
  | Components
  |--------------------------------------------------
  */
  components: {
    Loader,
    UsageCard,
    AccountUsageDonutChart
  },
  /**
  |--------------------------------------------------
  | Methods
  |--------------------------------------------------
  */
  methods: {
    getSelectedFilterDayDate,
    computeUsage(data) {
      if (isEmpty(data.data)) return 0;

      return data.data.reduce((prev, curr) => {
        return (prev += curr.volume);
      }, 0);
    },
    async getTicketsUsage() {
      try {
        this.isLoading = true;

        const { data } = await accounts.usage(
          { params: this.payload },
          this.currentAccount.uid
        );

        this.ticketsStats.used = this.computeUsage(data);
      } finally {
        this.isLoading = false;
      }
    }
  },
  /**
  |--------------------------------------------------
  | Mounted Lifecycle
  |--------------------------------------------------
  */
  mounted() {
    this.getTicketsUsage();
  }
};
</script>
<style scoped>
  #service-cloud-usage {
    margin-top: 0px;
  }
</style>